import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Ações secundárias (exportar, importar, filtros de período…). */
  actions?: ReactNode;
  /**
   * Ação principal da tela (uma só). No desktop fica à direita das demais ações;
   * no mobile vai para uma barra fixa no rodapé, acima da navegação inferior.
   */
  primaryAction?: ReactNode;
  /** Rota de retorno (telas de detalhe/edição): exibe um botão "voltar" antes do título. */
  backTo?: string;
  /** Selos ao lado do título (situação, tipo…). */
  badges?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  primaryAction,
  backTo,
  badges,
  className,
}: PageHeaderProps) {
  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6',
          className,
        )}
      >
        <div className="flex min-w-0 items-start gap-2">
          {backTo ? (
            <Link
              to={backTo}
              aria-label="Voltar"
              className="-ml-2 inline-flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-primary/25 focus-visible:outline-none"
            >
              <ArrowLeft className="size-[18px]" />
            </Link>
          ) : null}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 className="text-xl leading-8 font-semibold tracking-tight text-foreground md:text-[22px]">
                {title}
              </h1>
              {badges}
            </div>
            {description ? (
              <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>
        {actions || primaryAction ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
            {actions}
            {primaryAction ? <div className="hidden md:contents">{primaryAction}</div> : null}
          </div>
        ) : null}
      </div>
      {primaryAction ? <MobileActionBar>{primaryAction}</MobileActionBar> : null}
    </>
  );
}

/**
 * Barra fixa no rodapé (só mobile) com a ação principal. Fica acima da navegação inferior e
 * da área segura; o `AppShell` reserva espaço no fim do conteúdo quando ela existe. Em telas com
 * rodapé de formulário (`FormActions`) ela se oculta, para não empilhar duas barras fixas.
 */
export function MobileActionBar({ children }: { children: ReactNode }) {
  return (
    <div
      data-mobile-action-bar
      className="[main:has([data-form-actions])_&]:hidden fixed inset-x-0 bottom-(--mobile-bottom-offset) z-30 flex gap-2 border-t bg-card/95 px-4 py-2.5 backdrop-blur md:hidden [&>*]:h-11 [&>*]:flex-1 [&>*]:rounded-lg [&>*]:text-[15px]"
    >
      {children}
    </div>
  );
}
